import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ParteSelector } from "../components/CrediusParti";
import { toast } from "sonner";
import { Scale, ArrowLeft, X, Loader2, Send } from "lucide-react";

const SEL = "h-12 w-full border-2 border-slate-300 rounded-lg px-3 bg-white text-base mt-1";

const EMPTY_SOGG = { nome: "", tipo_persona: "Persona fisica", codice_fiscale: "", indirizzo: "", citta: "", cap: "", provincia: "", telefono: "", email: "", pec: "", note: "" };

export default function CrediusNuova() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [condomini, setCondomini] = useState([]);
  const [residenti, setResidenti] = useState([]);
  const [soggetti, setSoggetti] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [form, setForm] = useState({ tipo: "stragiudiziale", oggetto: "", descrizione: "", settore: "", avvocato_fornitore_id: "", coaffidatario_fornitore_id: "" });
  const [clienti, setClienti] = useState([]);
  const [controparti, setControparti] = useState([]);
  const [busy, setBusy] = useState(false);
  const [nuovoSettore, setNuovoSettore] = useState("");
  const [showSettore, setShowSettore] = useState(false);
  const [soggDialog, setSoggDialog] = useState(null); // "clienti" | "controparti" | null
  const [sogg, setSogg] = useState(EMPTY_SOGG);

  const loadSoggetti = () => api.get("/credius/soggetti").then((r) => setSoggetti(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/credius/meta").then((r) => setMeta(r.data)).catch(() => setMeta({}));
    Promise.all([api.get("/condomini"), api.get("/residenti"), api.get("/fornitori")]).then(([c, r, f]) => {
      setCondomini(c.data);
      const byId = Object.fromEntries(c.data.map((x) => [x.id, x.nome]));
      setResidenti(r.data.map((x) => ({ ...x, condominio_nome: byId[x.condominio_id] || "" })));
      setFornitori(Array.isArray(f.data) ? f.data : []);
    }).catch(() => {});
    loadSoggetti();
  }, []);

  const addSettore = async () => {
    const nome = nuovoSettore.trim();
    if (!nome) return;
    try {
      const { data } = await api.post("/credius/settori", { nome });
      setMeta((m) => ({ ...m, settori: data.settori }));
      setForm((f) => ({ ...f, settore: nome }));
      setNuovoSettore(""); setShowSettore(false);
      toast.success("Settore aggiunto");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore"); }
  };

  const salvaSoggetto = async () => {
    if (!sogg.nome.trim()) { toast.error("Indica il nome del soggetto"); return; }
    try {
      // Iter122j · Step 1 · quando si crea da "Clienti"/"Controparti" imposta il ruolo di default
      const ruoloDefault = soggDialog === "clienti" ? "cliente" : "controparte";
      const { data } = await api.post("/credius/soggetti", { ...sogg, ruolo_default: ruoloDefault });
      await loadSoggetti();
      const parte = { tipo: "soggetto", ref_id: data.id, nome: data.nome, dettaglio: data.citta || "" };
      if (soggDialog === "clienti") setClienti((p) => [...p, parte]);
      else setControparti((p) => [...p, parte]);
      setSoggDialog(null); setSogg(EMPTY_SOGG);
      toast.success("Soggetto creato e aggiunto");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore"); }
  };

  const salva = async (affidando) => {
    if (!form.oggetto.trim()) { toast.error("Indica l'oggetto della pratica"); return; }
    if (clienti.length === 0) { toast.error("Indica almeno un cliente"); return; }
    if (affidando && !form.avvocato_fornitore_id) {
      toast.error("Seleziona l'avvocato affidatario oppure salva senza affidare");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, clienti, controparti };
      if (!affidando) { payload.avvocato_fornitore_id = ""; payload.coaffidatario_fornitore_id = ""; }
      const { data } = await api.post("/credius/pratiche", payload);
      if (affidando) toast.success(`Pratica ${data.numero} affidata a ${data.avvocato_nome}`);
      else toast.success(`Pratica ${data.numero} salvata in stato "Da affidare"`);
      navigate(`/credius/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Creazione non riuscita");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="credius-nuova-page" className="max-w-3xl mx-auto">
      <button type="button" onClick={() => navigate("/credius")} data-testid="credius-back"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-indigo-900 mb-3">
        <ArrowLeft className="w-4 h-4" /> Torna a Credius
      </button>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 rounded-2xl bg-indigo-900 flex items-center justify-center"><Scale className="w-6 h-6 text-[#D4AF5A]" /></div>
        <div>
          <h1 className="text-2xl font-bold text-indigo-950">Nuova pratica — Parti</h1>
          <p className="text-sm text-slate-500">Sezione Affido · compila i dati e affida la pratica all'avvocato</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border-2 border-slate-200 p-5 space-y-4">
        <div>
          <Label>Tipo di pratica</Label>
          <div className="flex gap-2 mt-1">
            {["stragiudiziale", "giudiziale", "sinistro"].map((t) => (
              <button key={t} type="button" data-testid={`credius-tipo-${t}`}
                onClick={() => setForm({ ...form, tipo: t })}
                className={`px-4 h-11 rounded-lg border-2 font-semibold capitalize ${form.tipo === t ? "border-indigo-800 bg-indigo-900 text-white" : "border-slate-300 bg-white text-slate-600"}`}>
                {t}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label>Oggetto pratica *</Label>
          <Input data-testid="credius-oggetto" value={form.oggetto} onChange={(e) => setForm({ ...form, oggetto: e.target.value })}
            placeholder="es. Recupero credito quote condominiali" className="h-12 mt-1" />
        </div>
        <div>
          <Label>Breve descrizione della controversia</Label>
          <Textarea data-testid="credius-descrizione" value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
            rows={3} className="mt-1" placeholder="Descrivi sinteticamente la controversia..." />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Avvocato affidatario <span className="text-slate-400 text-xs font-normal">(opzionale — puoi salvare senza affidare)</span></Label>
            <select data-testid="credius-avvocato" value={form.avvocato_fornitore_id}
              onChange={(e) => setForm({ ...form, avvocato_fornitore_id: e.target.value })} className={SEL}>
              <option value="">— Seleziona —</option>
              {(meta?.avvocati || []).map((a) => <option key={a.id} value={a.id}>{a.ragione_sociale}</option>)}
            </select>
            {meta && (meta.avvocati || []).length === 0 && (
              <p className="text-xs text-amber-700 mt-1">Nessun fornitore con categoria "Avvocato-Studi Legali": aggiungilo in Account Fornitori.</p>
            )}
          </div>
          {/* Iter122ag · co-affidatario (opzionale, deve essere diverso dall'affidatario principale) */}
          <div>
            <Label>Co-affidatario <span className="text-slate-400 text-xs font-normal">(opzionale — stesso potere operativo)</span></Label>
            <select data-testid="credius-coaffidatario" value={form.coaffidatario_fornitore_id}
              disabled={!form.avvocato_fornitore_id}
              onChange={(e) => setForm({ ...form, coaffidatario_fornitore_id: e.target.value })} className={SEL}>
              <option value="">— Nessuno —</option>
              {(meta?.avvocati || []).filter((a) => a.id !== form.avvocato_fornitore_id).map((a) => <option key={a.id} value={a.id}>{a.ragione_sociale}</option>)}
            </select>
            {!form.avvocato_fornitore_id && <p className="text-xs text-slate-500 mt-1">Seleziona prima l'affidatario principale.</p>}
          </div>
          <div>
            <Label>Settore giuridico</Label>
            <select data-testid="credius-settore" value={form.settore}
              onChange={(e) => setForm({ ...form, settore: e.target.value })} className={SEL}>
              <option value="">— Seleziona —</option>
              {(meta?.settori || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {showSettore ? (
              <div className="flex gap-2 mt-2">
                <Input data-testid="credius-settore-nuovo" value={nuovoSettore} onChange={(e) => setNuovoSettore(e.target.value)}
                  placeholder="Nuovo settore..." className="h-10" />
                <Button size="sm" className="h-10 bg-indigo-900 hover:bg-indigo-950" data-testid="credius-settore-add" onClick={addSettore}>Aggiungi</Button>
                <Button size="sm" variant="ghost" className="h-10" onClick={() => setShowSettore(false)}><X className="w-4 h-4" /></Button>
              </div>
            ) : (
              <button type="button" data-testid="credius-settore-toggle" onClick={() => setShowSettore(true)}
                className="text-xs font-semibold text-indigo-700 hover:underline mt-1">+ Aggiungi settore giuridico</button>
            )}
          </div>
        </div>

        <ParteSelector label="Clienti *" parti={clienti} setParti={setClienti}
          condomini={condomini} residenti={residenti} soggetti={soggetti} fornitori={fornitori}
          sources={[
            { key: "condominio", label: "Condominio" },
            { key: "condomino", label: "Condòmino (anagrafica presente)" },
            { key: "fornitore", label: "Fornitore Studio" },
            { key: "soggetto", label: "Altri clienti" },
          ]}
          onNuovo={() => setSoggDialog("clienti")} testid="credius-clienti" />

        {(() => {
          // Iter122k7 · Se ci sono uno o più Condomìni tra i Clienti, il menù a tendina
          // "Condòmino" della Controparte mostra SOLO i condòmini di quei condomini
          // (filtro sul dropdown, nessuna pre-selezione — l'utente sceglie quali aggiungere).
          const clientiCondominiIds = clienti.filter((c) => c.tipo === "condominio").map((c) => c.ref_id);
          const clientiCondominiNomi = clienti.filter((c) => c.tipo === "condominio").map((c) => c.nome);
          const restrictControparti = clientiCondominiIds.length > 0;
          const residentiForControparti = restrictControparti
            ? residenti.filter((r) => clientiCondominiIds.includes(r.condominio_id))
            : residenti;
          const controparteSources = restrictControparti
            ? [
                { key: "condomino", label: `Condòmini di ${clientiCondominiNomi.join(", ")}` },
                { key: "condomino_tutti", label: "Tutti i condòmini in anagrafica" },
                { key: "fornitore", label: "Fornitore Studio" },
                { key: "soggetto", label: "Altri soggetti" },
              ]
            : [
                { key: "condomino", label: "Condòmini già presenti in anagrafica" },
                { key: "fornitore", label: "Fornitore Studio" },
                { key: "soggetto", label: "Altri soggetti" },
                { key: "tutti", label: "Tutti" },
              ];
          return (
            <div>
              {restrictControparti && (
                <div data-testid="credius-controparti-restrict-hint"
                  className="mb-2 rounded-lg border-2 border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
                  <b>Filtro attivo:</b> il menù "Condòmino" mostra solo i condòmini registrati in <b>{clientiCondominiNomi.join(", ")}</b> ({residentiForControparti.length} disponibili). Puoi comunque passare a <b>"Tutti i condòmini in anagrafica"</b> per scegliere fuori dal condominio.
                </div>
              )}
              <ParteSelector label="Controparti" parti={controparti} setParti={setControparti}
                condomini={condomini} residenti={residentiForControparti} residentiAll={residenti}
                soggetti={soggetti} fornitori={fornitori}
                sources={controparteSources}
                onNuovo={() => setSoggDialog("controparti")} testid="credius-controparti" />
            </div>
          );
        })()}

        <div className="actions-row flex flex-col sm:flex-row flex-wrap gap-3 pt-2 sm:justify-start">
          <Button data-testid="credius-salva-btn" onClick={() => salva(false)} disabled={busy}
            variant="outline" className="h-12 px-5 border-slate-400 text-slate-700 font-bold w-full sm:w-auto">
            {busy ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : null} Salva senza affidare
          </Button>
          <Button data-testid="credius-affida-btn" onClick={() => salva(true)} disabled={busy}
            className="h-12 px-6 bg-indigo-900 hover:bg-indigo-950 text-base font-bold w-full sm:w-auto">
            {busy ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Send className="w-5 h-5 mr-2" />} Salva e affida
          </Button>
          <Button variant="outline" className="h-12 w-full sm:w-auto" onClick={() => navigate("/credius")}>Annulla</Button>
        </div>
      </div>

      {soggDialog && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="credius-soggetto-dialog">
          <div className="bg-white rounded-xl max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-lg text-indigo-950">Nuovo soggetto ({soggDialog === "clienti" ? "cliente" : "controparte"})</h2>
              <button type="button" onClick={() => setSoggDialog(null)}><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <Label>Nominativo / Ragione sociale *</Label>
                <Input data-testid="sogg-nome" value={sogg.nome} onChange={(e) => setSogg({ ...sogg, nome: e.target.value })} className="h-11 mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo</Label>
                  <select value={sogg.tipo_persona} onChange={(e) => setSogg({ ...sogg, tipo_persona: e.target.value })}
                    className="h-11 w-full border-2 border-slate-300 rounded-lg px-3 bg-white mt-1">
                    <option>Persona fisica</option><option>Persona giuridica</option>
                  </select>
                </div>
                <div>
                  <Label>Codice fiscale / P.IVA</Label>
                  <Input data-testid="sogg-cf" value={sogg.codice_fiscale} onChange={(e) => setSogg({ ...sogg, codice_fiscale: e.target.value.toUpperCase() })} className="h-11 mt-1" />
                </div>
              </div>
              <div>
                <Label>Indirizzo</Label>
                <Input value={sogg.indirizzo} onChange={(e) => setSogg({ ...sogg, indirizzo: e.target.value })} className="h-11 mt-1" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2"><Label>Città</Label>
                  <Input data-testid="sogg-citta" value={sogg.citta} onChange={(e) => setSogg({ ...sogg, citta: e.target.value })} className="h-11 mt-1" /></div>
                <div><Label>CAP</Label>
                  <Input value={sogg.cap} onChange={(e) => setSogg({ ...sogg, cap: e.target.value })} className="h-11 mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Telefono</Label>
                  <Input value={sogg.telefono} onChange={(e) => setSogg({ ...sogg, telefono: e.target.value })} className="h-11 mt-1" /></div>
                <div><Label>Email</Label>
                  <Input value={sogg.email} onChange={(e) => setSogg({ ...sogg, email: e.target.value })} className="h-11 mt-1" /></div>
              </div>
              <div><Label>PEC</Label>
                <Input value={sogg.pec} onChange={(e) => setSogg({ ...sogg, pec: e.target.value })} className="h-11 mt-1" /></div>
              {/* Iter122k28 · Campo Note nel form "Aggiungi cliente/controparte" */}
              <div><Label>Note</Label>
                <Textarea data-testid="sogg-note" rows={2} value={sogg.note || ""}
                  onChange={(e) => setSogg({ ...sogg, note: e.target.value })}
                  placeholder="Note interne sul soggetto (visibili solo allo Studio)"
                  className="mt-1" /></div>
              <div className="actions-row flex gap-3 pt-1">
                <Button data-testid="sogg-salva" onClick={salvaSoggetto} className="h-11 bg-indigo-900 hover:bg-indigo-950">Salva e aggiungi</Button>
                <Button variant="outline" className="h-11" onClick={() => setSoggDialog(null)}>Annulla</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
